import { useFormUtils } from "@/app/hooks/useFormUtils";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React, { Fragment, useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import FormFieldErrors from "../../Reusable/FormFieldErrors";
import { ISearchableSelectMenu, ISelectMenuItemData, SelectMenuItems } from "../../interface";

const CitySelectMenu: React.FC<ISearchableSelectMenu> = ({
  fieldError,
  placeholder,
  defaultSelected,
  className,
  itemsClassName,
  showDropdownIcon,
  errorMessages,
  showDropdownWhenNoData,
  showTypedErrors,
  showTooltips,
  register,
  disableSearch,
  isMultiSelect,
  onSelectItem,
  listBoxClassName,
  onInputChange,
  ...props
}) => {
  const { deduceFormFieldErrors, isFormFieldValid } = useFormUtils();
  const [selected, setSelected] = useState<ISelectMenuItemData | null>(null);
  const [query, setQuery] = useState<string>("");

  const filteredDataItems: SelectMenuItems =
    query === ""
      ? props?.selectItems
      : props?.selectItems.filter((item) =>
          item?.title?.toLowerCase()?.replace(/\s+/g, "")?.includes(query.toLowerCase()?.replace(/\s+/g, "")),
        );

  const reducedErrorMessages = deduceFormFieldErrors(errorMessages, fieldError);
  const isValid: boolean = isFormFieldValid(reducedErrorMessages);

  useEffect(() => {
    if (defaultSelected === undefined) {
      setSelected(null);
    } else [setSelected(defaultSelected)];
  });

  const showFieldErrors = showTypedErrors && showTypedErrors === true ? true : showTypedErrors === false ? false : true;

  return (
    <div style={props?.style ?? {}} className={props?.containerClassName ?? ""}>
      <Combobox
        disabled={props?.disabled && props?.disabled === true ? true : false}
        value={selected}
        onChange={(item) => {
          setSelected(item);
          if (onSelectItem) {
            onSelectItem(item);
          }
        }}
        nullable
      >
        <div className="relative  w-full">
          <div
            className={clsx(
              "relative w-full cursor-default overflow-hidden rounded-lg border border-gray-300 bg-white text-left  focus:outline-none   sm:text-sm ",
              !isValid && "border-none border-transparent ",
            )}
          >
            <input {...register} hidden />
            {disableSearch && disableSearch === true ? (
              <>
                <Combobox.Button className="w-full">
                  <Combobox.Input
                    readOnly
                    autoComplete="false"
                    autoCorrect="false"
                    placeholder={placeholder}
                    displayValue={(item: ISelectMenuItemData): string => {
                      return item?.title;
                    }}
                    className={clsx(
                      `w-full text-left text-xs font-medium  rounded-lg py-2 pl-3 pr-10 text-gray-500 leading-5  focus:ring-0 ${className}`,
                      isValid
                        ? "text-gray-500 border-none outline-none font-medium"
                        : "text-red-600  border border-red-500  outline-red-500 font-medium",
                      props?.disabled && props?.disabled === true && "bg-gray-200",
                    )}
                    onChange={(event) => {
                      setQuery(event.target.value);
                    }}
                  />
                </Combobox.Button>
              </>
            ) : (
              <Combobox.Button className="w-full">
                <Combobox.Input
                  autoComplete="off"
                  autoCorrect="false"
                  className={clsx(
                    `w-full text-xs font-medium  rounded-lg py-2 pl-3 pr-10 leading-5  focus:ring-0 ${className}`,
                    isValid
                      ? "text-gray-500 border-none outline-none font-medium "
                      : "text-red-600  border border-red-500 outline-red-500 ",
                    props?.disabled && props?.disabled === true && "bg-gray-200",
                  )}
                  placeholder={placeholder}
                  displayValue={(item: ISelectMenuItemData): string => {
                    return item?.title;
                  }}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    if (onInputChange) {
                      onInputChange(event.target.value);
                    }
                  }}
                />
              </Combobox.Button>
            )}
            {showDropdownIcon && showDropdownIcon === true ? (
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </Combobox.Button>
            ) : (
              <></>
            )}
          </div>
          {showFieldErrors && (
            <div className="">
              <FormFieldErrors errors={reducedErrorMessages} />
            </div>
          )}
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              style={{ zIndex: 1 }}
              className={clsx(
                filteredDataItems?.length > 0 &&
                  "absolute  mt-1  overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none max-h-60 sm:text-sm",
                listBoxClassName ? listBoxClassName : "w-full",
              )}
            >
              {filteredDataItems?.length === 0
                ? showDropdownWhenNoData &&
                  showDropdownWhenNoData === true && (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No Items</div>
                  )
                : filteredDataItems?.map((item: ISelectMenuItemData) => (
                    <Combobox.Option
                      key={item.id}
                      className={({ selected }) =>
                        clsx(
                          "relative flex font-medium  pl-2 text-xs justify-between items-center cursor-default select-none py-2 text-center ",
                          selected
                            ? "bg-gray-800 hover:bg-gray-800 text-white font-medium "
                            : "text-gray-800 hover:bg-gray-800 hover:text-white",
                          itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                        )
                      }
                      onClick={() => setSelected(item)}
                      data-tooltip-place="right"
                      data-tooltip-id={`option-tooltip:${register?.name}`}
                      data-tooltip-content={item.title}
                      value={item}
                    >
                      {({ selected, active }) => (
                        <>
                          {isMultiSelect && isMultiSelect === true && (
                            <input
                              id="comments"
                              aria-describedby="comments-description"
                              name="comments"
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            />
                          )}
                          <span className={`block text-left truncate ${selected ? "font-medium " : "font-normal"}`}>
                            {item.title} {props?.extraInfoNode && <span>{props?.extraInfoNode}</span>}
                          </span>
                          {selected ? (
                            <div className="flex  text-left justify-start items-center">
                              <span
                                className={`inset-y-0 text-left left-0 flex items-center pl-3 ${
                                  active ? "text-white" : "text-blue-600"
                                }`}
                              ></span>
                              <CheckIcon className="w-4 h-4 mr-4" />
                            </div>
                          ) : null}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
      {showTooltips && showTooltips === true && (
        <Tooltip id={`option-tooltip:${register?.name}`} style={{ zIndex: 5 }} />
      )}
    </div>
  );
};

export default CitySelectMenu;
